import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
} from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useBreadcrumbs } from "@/contexts/BreadcrumbContext";
import { logger } from "@/utils/logger";
import { 
  MoreHorizontal, 
  UserPlus, 
  UserMinus, 
  User, 
  AlertTriangle,
  CheckCircle,
  FileText,
  UserCircle
} from "lucide-react";
import { useAuth } from "@/hooks/useAuth";

interface UserInfo {
  username: string;
  name?: string | null;
  avatar_url?: string | null;
}

interface AssignmentInfo {
  assigned_to: UserInfo;
  assigned_by: UserInfo;
  assigned_at: string;
}

interface ReleaseNoteSummarySchema {
  _id: string;
  title: string;
  version?: string | null;
  url?: string | null;
  assigned_to?: string | null;
  assigned_user_info?: AssignmentInfo | null;
  item_count: number;
  update_needed_count: number;
}

interface PaginatedNotesSchema {
  total_notes: number;
  notes: ReleaseNoteSummarySchema[];
  page: number;
  page_size: number;
  total_pages: number;
  // available_release_notes?: Record<string, string>; // Might not be needed here
}

// Rename props interface
interface ReleaseNotesProps {
  apiBaseUrl: string;
}

// --- Helper Function to group notes by year ---
const groupNotesByYear = (notes: ReleaseNoteSummarySchema[]) => {
  const grouped: Record<string, ReleaseNoteSummarySchema[]> = {};
  notes.forEach((note) => {
    let year = "Unknown"; // Default category
    if (note.version && typeof note.version === "string") {
      const parts = note.version.split(".");
      if (parts.length > 0 && !isNaN(parseInt(parts[0], 10))) {
        const yearPrefix = parseInt(parts[0], 10);
        if (yearPrefix >= 0 && yearPrefix <= 99) {
          year = `20${yearPrefix.toString().padStart(2, "0")}`; // Convert YY to 20YY
        }
      }
    }
    if (!grouped[year]) {
      grouped[year] = [];
    }
    grouped[year].push(note);
  });

  // Sort notes within each year (optional, assuming fetchNotes already sorts)
  // Object.keys(grouped).forEach(year => {
  //   grouped[year].sort(...); // Add sorting logic if needed
  // });

  return grouped;
};

// Rename component function and props interface type
const ReleaseNotes: React.FC<ReleaseNotesProps> = ({ apiBaseUrl }) => {
  const [notes, setNotes] = useState<ReleaseNoteSummarySchema[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [assigningNoteId, setAssigningNoteId] = useState<string | null>(null);
  const { setBreadcrumbs } = useBreadcrumbs();
  const { user } = useAuth();
  const navigate = useNavigate();

  // Create a reference array for section IDs
  const sectionRefs = useRef<{ id: string; title: string }[]>([]);

  const fetchNotes = useCallback(async () => {
    setLoading(true);
    setError(null);
    const params = new URLSearchParams({
      sort_by_version: "true", // Default sort
      // Add other filter/sort params here
    });

    try {
      const response = await axios.get<PaginatedNotesSchema>(
        `${apiBaseUrl}/release-notes-summary`,
        { params }
      );
      setNotes(response.data.notes);
    } catch (err) {
      logger.error("Error fetching release notes summary", {
        error: err instanceof Error ? err.message : String(err),
        url: `${apiBaseUrl}/release-notes-summary`,
        params: Object.fromEntries(params),
      });
      let errorMsg = "Failed to load release notes.";
      if (axios.isAxiosError(err) && err.response) {
        errorMsg = `Failed to load notes: ${
          err.response.data?.detail || err.message
        }`;
      } else if (err instanceof Error) {
        errorMsg = `Failed to load notes: ${err.message}`;
      }
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  }, [apiBaseUrl]);

  // Handle assign to me
  const handleAssign = async (noteId: string) => {
    if (!user?.username) {
      setError("You must be logged in to assign release notes.");
      return;
    }

    setAssigningNoteId(noteId);
    try {
      // Assuming the backend returns the updated note or confirmation
      const response = await axios.post<{ assigned_user_info: AssignmentInfo }>(`${apiBaseUrl}/release-note/${noteId}/assign`, {
        assigned_to: user.username
      });

      // Update the notes in the UI with potentially more complete info from backend
      setNotes(prev => prev.map(note =>
        note._id === noteId
          ? { ...note, assigned_to: user?.username, assigned_user_info: response.data.assigned_user_info }
          : note
      ));
    } catch (err) {
      logger.error("Error assigning release note", {
        error: err instanceof Error ? err.message : String(err),
        noteId,
        username: user?.username,
      });
      let errorMsg = "Failed to assign release note.";
      if (axios.isAxiosError(err) && err.response) {
        errorMsg = `Failed to assign note: ${
          err.response.data?.detail || err.message
        }`;
      } else if (err instanceof Error) {
        errorMsg = `Failed to assign note: ${err.message}`;
      }
      setError(errorMsg);
    } finally {
      setAssigningNoteId(null);
    }
  };

  // Handle unassign
  const handleUnassign = async (noteId: string) => {
    setAssigningNoteId(noteId);
    try {
      await axios.post(`${apiBaseUrl}/release-note/${noteId}/unassign`);

      // Update the notes in the UI
      setNotes(prev => prev.map(note =>
        note._id === noteId
          ? { ...note, assigned_to: null, assigned_user_info: null } // Clear assignment info
          : note
      ));
    } catch (err) {
      logger.error("Error unassigning release note", {
        error: err instanceof Error ? err.message : String(err),
        noteId,
      });
      let errorMsg = "Failed to unassign release note.";
      if (axios.isAxiosError(err) && err.response) {
        errorMsg = `Failed to unassign note: ${
          err.response.data?.detail || err.message
        }`;
      } else if (err instanceof Error) {
        errorMsg = `Failed to unassign note: ${err.message}`;
      }
      setError(errorMsg);
    } finally {
      setAssigningNoteId(null);
    }
  };

  // Render the assignment dropdown menu using daisyUI dropdown
  const renderAssignmentDropdown = (note: ReleaseNoteSummarySchema) => {
    const isAssignedToMe = note.assigned_to === user?.username;
    const isAssignedToSomeone = note.assigned_to !== null && note.assigned_to !== undefined;
    const isProcessing = assigningNoteId === note._id;

    if (isProcessing) {
      return (
        <div className="flex items-center justify-center p-2"> {/* Adjusted padding */}
          <span className="loading loading-spinner loading-xs"></span> {/* daisyUI spinner */}
        </div>
      );
    }

    // Format assignment date if available
    let assignmentInfo = "";
    if (note.assigned_user_info?.assigned_at) {
      try {
        const assignDate = new Date(note.assigned_user_info.assigned_at);
        const formattedDate = assignDate.toLocaleDateString('sv-SE', { year: 'numeric', month: 'numeric', day: 'numeric' });
        const assignerName = note.assigned_user_info?.assigned_by?.name ||
                             note.assigned_user_info?.assigned_by?.username ||
                             "Unknown";
        assignmentInfo = `By ${assignerName} on ${formattedDate}`;
      } catch (err) {
        console.warn("Error formatting assignment date", err);
      }
    }

    const assignedToName = note.assigned_user_info?.assigned_to?.name ||
                           note.assigned_user_info?.assigned_to?.username ||
                           note.assigned_to ||
                           "Unknown";

    return (
      <div className="dropdown dropdown-end"> {/* daisyUI dropdown container */}
        <button tabIndex={0} role="button" className="btn btn-ghost btn-xs p-1 m-1"> {/* daisyUI button trigger */}
          <MoreHorizontal className="h-4 w-4" />
        </button>
        <ul tabIndex={0} className="dropdown-content z-[1] menu p-2 shadow bg-base-200 rounded-box w-52"> {/* daisyUI menu */}
          <li><span className="menu-title">Assignment</span></li>
          {isAssignedToMe ? (
            <>
              <li>
                <span className="opacity-70 cursor-default"> {/* Use opacity or specific text color */}
                  <User className="mr-2 h-4 w-4" />
                  Assigned to me
                </span>
              </li>
              {assignmentInfo && (
                <li>
                  <span className="opacity-70 text-xs cursor-default px-6">{assignmentInfo}</span>
                </li>
              )}
               <li><div className="divider my-0"></div></li>{/* daisyUI separator */}
              <li>
                <a onClick={() => handleUnassign(note._id)}>
                  <UserMinus className="mr-2 h-4 w-4" />
                  Unassign
                </a>
              </li>
            </>
          ) : isAssignedToSomeone ? (
            <>
              <li>
                <span className="opacity-70 cursor-default">
                  <User className="mr-2 h-4 w-4" />
                  Assigned to {assignedToName}
                </span>
              </li>
              {assignmentInfo && (
                 <li>
                   <span className="opacity-70 text-xs cursor-default px-6">{assignmentInfo}</span>
                 </li>
              )}
               <li><div className="divider my-0"></div></li>{/* daisyUI separator */}
              <li>
                <a onClick={() => handleAssign(note._id)}>
                  <UserPlus className="mr-2 h-4 w-4" />
                  Assign to me
                </a>
              </li>
              <li>
                <a onClick={() => handleUnassign(note._id)}>
                  <UserMinus className="mr-2 h-4 w-4" />
                  Unassign
                </a>
              </li>
            </>
          ) : (
            <li>
              <a onClick={() => handleAssign(note._id)}>
                <UserPlus className="mr-2 h-4 w-4" />
                Assign to me
              </a>
            </li>
          )}
        </ul>
      </div>
    );
  };

  useEffect(() => {
    fetchNotes();
  }, [fetchNotes]);

  useEffect(() => {
    setBreadcrumbs([{ label: "Release Notes", href: "/release-notes" }]);
  }, [setBreadcrumbs]);

  const groupedNotes = useMemo(() => groupNotesByYear(notes), [notes]);
  const sortedYears = useMemo(
    () =>
      Object.keys(groupedNotes).sort(
        (a, b) => parseInt(b, 10) - parseInt(a, 10)
      ),
    [groupedNotes]
  );

  useEffect(() => {
    if (notes.length > 0) {
      sectionRefs.current = [
        { id: "overview", title: "Overview" },
        ...notes.map((note, index) => ({
          id: `release-${index}`, // Ensure IDs are unique if needed elsewhere
          title: note.title,
        })),
      ];
    }
  }, [notes]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <span className="loading loading-spinner loading-lg"></span> {/* daisyUI spinner */}
        <p className="ml-4 text-base-content/70">Loading release notes...</p> {/* Use theme color */}
      </div>
    );
  }

  if (error) {
    return (
       // daisyUI Alert
      <div role="alert" className="alert alert-error max-w-2xl mx-auto mt-8">
        <AlertTriangle className="h-6 w-6" /> {/* Adjusted icon size */}
        <span>{error}</span>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto">
      {/* Header Section */}

      {/* Main content with table of contents */}
      <div className="flex flex-col md:flex-row gap-6 p-6">
        {/* Main content - Release notes list */}
        <div className="flex-grow order-1 md:order-2 space-y-6">
           {/* Removed complex dark mode gradient, rely on theme */}
          <h2 className="text-2xl font-bold flex items-center">
            Release Notes
          </h2>

          {notes.length > 0 ? (
             // Use daisyUI Card styling
            <div className="card bg-base-100/70 backdrop-blur-sm shadow-md border border-base-300/50 overflow-hidden">
              <div className="card-body p-0"> {/* No padding on card body to allow table full width */}
                 {/* daisyUI Table */}
                <table className="table w-full">
                  <thead> {/* Use thead */}
                    <tr> {/* Use tr */}
                      <th className="w-[50%]">Title</th> {/* Use th */}
                      <th className="text-center">Items</th>
                      <th className="text-center">Updates Needed</th>
                      <th className="text-center">Assigned To</th>
                      <th className="text-center w-[60px]">Actions</th>
                    </tr>
                  </thead>
                  <tbody> {/* Use tbody */}
                    {sortedYears.map((year) => (
                      <React.Fragment key={year}>
                        {/* Year Subheader */}
                        <tr>
                          <td
                            colSpan={5}
                            className="font-semibold text-primary pt-5 px-4" // Use daisyUI primary color
                          >
                            {year}
                          </td>
                        </tr>
                        {/* Notes for the year */}
                        {groupedNotes[year].map((note, index) => (
                          <tr // Use tr
                            key={note._id}
                            data-section-id={`release-${index}`} // Keep data attribute if used
                            className={`
                              cursor-pointer hover:bg-base-200 transition-colors
                              ${note.update_needed_count === 0 ? 'bg-success/10 hover:bg-success/20' : ''} // Conditional green tint using daisyUI success color
                            `}
                            onClick={() =>
                              navigate(`/release-notes/${note._id}`)
                            }
                          >
                            <td // Use td
                              className="font-medium pl-6"
                              id={`release-${index}`} // Keep id if used
                            >
                              {note.title}
                            </td>
                            <td className="text-center"> {/* Use td */}
                              {note.item_count}
                            </td>
                            <td className="text-center"> {/* Use td */}
                              <div className="flex items-center justify-center gap-1">
                                {note.update_needed_count > 0 ? (
                                  <AlertTriangle className="h-4 w-4 text-warning" /> // Replaced ExclamationTriangleIcon
                                ) : (
                                  <CheckCircle className="h-4 w-4 text-success" /> // Replaced CheckCircleIcon
                                )}
                                <span>{note.update_needed_count}</span>
                              </div>
                            </td>
                            <td className="text-center"> {/* Use td */}
                              {note.assigned_to ? (
                                <div className="flex items-center justify-center gap-1">
                                  <UserCircle className="h-4 w-4 text-info" /> {/* Replaced UserCircleIcon */}
                                  <span className="text-sm">
                                    {note.assigned_user_info?.assigned_to?.name ||
                                     note.assigned_user_info?.assigned_to?.username ||
                                     note.assigned_to}
                                  </span>
                                </div>
                              ) : (
                                <span className="text-base-content/50">-</span> // Use theme color with opacity
                              )}
                            </td>
                            <td className="text-center p-0" onClick={(e) => e.stopPropagation()}> {/* Use td */}
                              {renderAssignmentDropdown(note)}
                            </td>
                          </tr>
                        ))}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <div className="text-center p-12 bg-base-200 border border-dashed border-base-300 rounded-lg"> {/* Use base colors */}
              <FileText className="h-12 w-12 text-base-content/40 mx-auto mb-4" /> {/* Replaced DocumentTextIcon */}
              <p className="text-base-content/70"> {/* Use theme color */}
                No release notes available.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ReleaseNotes;
